// Single source of truth for design values used across the app. Every value
// below was copied verbatim from where it already appeared in the codebase —
// nothing was invented, rounded, or "improved". See CLAUDE.md for the rule
// that all new inline styles must source values from here.
//
// Two documented exceptions, approved before this file was written:
//   - radius.full (9999) replaces two near-identical "fully round" values
//     (999 and 9999) that already rendered identically at their existing
//     element sizes — confirmed zero visual change.
//   - Every other pair of close-but-different values found while cataloguing
//     (letterSpacing 0.04/0.05/0.08em, the two box-shadow opacities, the
//     300ms/320ms flash timing) was kept SEPARATE rather than merged,
//     specifically to guarantee zero visual change. See the inline notes
//     on each for why they're close but not the same thing.
//
// Deliberately NOT covered here:
//   - frontend/src/index.css — plain CSS can't import a .ts module without
//     extra build tooling, so its hard-coded colors (#18181B, #FFFFFF,
//     #F4F4F5, #E0E7FF/indigo100, #FAFAFA, #4338CA/indigo700) are a second,
//     manually-kept-in-sync copy of values also listed below.
//   - Per-column table widths (the `width` field inside each table's COLS
//     array in MaterialTable.tsx / CatalogTable.tsx / etc.) — those are
//     content layout decisions specific to one table's schema, not shared
//     design-system values.
//   - components/ui/** (shadcn primitives) — they use Tailwind utility
//     classes and CSS variables, not hard-coded values, so there was
//     nothing to move.

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------
//
// ⚠️ THE KEY NAMES ARE LEGACY; THE VALUES ARE THE NEW PALETTE (slice
// ui-redesign, increment 1). Every hue below was repointed in place rather
// than renamed, and that was forced rather than chosen: `semanticColors.accent`
// has only TWO call sites in the whole app, while `colors.indigo600` has
// SEVENTY. The semantic layer is not, in practice, the accent hook — the raw
// palette key is. Repointing the value is therefore the only edit that
// recolours the app from the token layer alone, with zero component changes.
//
// So `indigo600` now holds a lime green and `gray200` holds a cool grey-blue.
// The names are wrong and are marked so; renaming them is a mechanical sweep
// over ~70 call sites and belongs in its own increment, not in one that is
// also introducing a shared modal shell.
//
// New palette, as supplied:
//   Canvas #F8FAF6 · Primary accent #9EE365 · Accent light #DCF7BE
//   Dark #1A1A1A · Mid text #5A5D61 · Light border #D4D9DE
//   Alert #ECA194 · In-progress #F6E79D
//
// Values NOT supplied by the palette (hover shades, the dark accent used for
// rings, the tint backgrounds and readable text tones for Alert/In-progress)
// were derived from the supplied hues and are commented individually with the
// contrast ratio that drove them.
export const colors = {
  white: "#FFFFFF",

  // --- Neutrals. Warm-green canvas + cool grey-blue lines and text. ---------
  /** @deprecated name — this is Canvas #F8FAF6. */
  gray50: "#F8FAF6",
  /** @deprecated name — hover/active fill, one step down from Canvas. */
  gray100: "#EFF3EC",
  /** @deprecated name — this is Light border #D4D9DE. */
  gray200: "#D4D9DE",
  /** @deprecated name — disabled fill. Dark text on it reads 9.5:1. */
  gray300: "#C2C8CE",
  /** @deprecated name — placeholder / decorative. 3.1:1 on Canvas: a hint
   *  tone, never the only label. Was 2.6:1 before, so this is an improvement,
   *  but it is deliberately below 4.5 and every field keeps a visible label. */
  gray400: "#8A9098",
  /** @deprecated name — muted label text. 5.2:1 on Canvas. */
  gray500: "#656B71",
  /** @deprecated name — this is Mid text #5A5D61. 6.4:1 on Canvas. */
  gray600: "#5A5D61",
  /** @deprecated name — this is Dark #1A1A1A. 17:1 on Canvas. */
  gray900: "#1A1A1A",

  // --- Accent. Lime, not indigo. -------------------------------------------
  /** @deprecated name — this is Accent light #DCF7BE. */
  indigo50: "#DCF7BE",
  /** @deprecated name — accent border / cell-flash start, between the two
   *  supplied greens. Mirrored in index.css's cellFlash keyframe. */
  indigo100: "#C6EF9C",
  /** @deprecated name — accent on a DARK surface (UndoBar's "Undo" link).
   *  12.5:1 on #1A1A1A. */
  indigo400: "#9EE365",
  /** @deprecated name — this is Primary accent #9EE365. Pair it with
   *  `onAccent` below, never with white: white on this is 1.6:1. */
  indigo600: "#9EE365",
  /** @deprecated name — primary-button hover/press. Mirrored in index.css. */
  indigo700: "#8AD653",
  /** @deprecated name — dark accent text on an accent-light tint. 7.5:1 on
   *  #DCF7BE. */
  indigo800: "#2E5A17",
  // Dark accent for 1px rings and indicator bars, where the bright accent
  // would be invisible: #9EE365 on white is 1.7:1, this is 4.6:1 (UI elements
  // need 3:1). Genuinely new — there was no equivalent before, because indigo
  // was dark enough to serve both jobs. Used by the two self-hex shadows.
  accentInk: "#47801F",
  // Text that sits ON the accent. 12.5:1 on #9EE365.
  onAccent: "#1A1A1A",

  // --- Success. NOT in the supplied palette, so deliberately unchanged. -----
  // Left as the existing teal-green: an "in stock" pill and the lime accent
  // are different meanings and must stay visually separable. Worth revisiting
  // in a later increment now that the accent is also green.
  green50: "#ECFDF5",
  green100: "#D1FAE5",
  green600: "#059669",

  // --- In-progress #F6E79D, expanded into a usable tint triplet. -----------
  amber50: "#FDF8E3", // derived tint background
  amber200: "#F6E79D", // the supplied In-progress hue — border/edge weight
  /** @deprecated name — readable In-progress text, 5.5:1 on Canvas. The
   *  supplied #F6E79D is 1.2:1 as text and cannot carry a message. */
  amber600: "#7A6410",
  amber800: "#5C4B0C", // darker still, for text on the amber50 tint

  // --- Alert #ECA194, expanded the same way. -------------------------------
  red50: "#FCEEEB", // derived tint background
  red100: "#F7D5CE", // derived border
  /** @deprecated name — readable Alert text, 6.2:1 on Canvas. The supplied
   *  #ECA194 is 2.0:1 as text; it is an edge/fill tone, not a message tone.
   *  Kept below as `alertEdge`. */
  red600: "#A33B27",
  alertEdge: "#ECA194", // the supplied Alert hue, for borders/fills only
} as const;

// Semantic aliases. Prefer these over the raw palette above — this is the
// role each color was already playing before this refactor.
export const semanticColors = {
  textPrimary: colors.gray900,
  textSecondary: colors.gray600,
  textMuted: colors.gray500, // uppercase field labels, sub-captions, inactive tab text
  textPlaceholder: colors.gray400, // search icon, disabled/empty-state text, lighter muted text
  textDisabled: colors.gray300, // toggle-off track, dashed "add" button border

  border: colors.gray200,
  surface: colors.white,
  panelBg: colors.gray50,
  // <main>'s own background only — distinct from panelBg above so raised
  // "card" surfaces (surface, white) read as a layer above the page canvas
  // instead of blending into it. panelBg keeps its existing job (filter
  // bars, info/warning strips) and is untouched.
  pageBg: colors.gray50,
  hoverBg: colors.gray100,

  accent: colors.indigo600, // Primary accent #9EE365
  accentBg: colors.indigo50, // Accent light #DCF7BE
  accentBorder: colors.indigo100,
  accentTextOnTint: colors.indigo800,
  accentLink: colors.indigo400,
  accentHover: colors.indigo700, // primary-button :hover/:active background
  // ⚠️ Text/icons sitting ON `accent`. The accent is a LIGHT green, so white
  // on it is 1.6:1 and unreadable — the one pairing that must never be made.
  // This is 12.5:1. Mirrored in index.css's `.btn-primary` colour rule, which
  // is what carries it to the 17 primary buttons that hard-code white inline.
  onAccent: colors.onAccent,
  // Dark accent for hairline rings and indicator bars, where the bright
  // accent disappears (1.7:1 on white). 4.6:1.
  accentInk: colors.accentInk,

  success: colors.green600,
  successBg: colors.green50,
  successBorder: colors.green100,

  warning: colors.amber600,
  warningBg: colors.amber50,
  warningBorder: colors.amber200,
  warningTextOnTint: colors.amber800,

  destructive: colors.red600,
  destructiveBg: colors.red50,
  destructiveBorder: colors.red100,
  // The supplied Alert hue #ECA194 itself. A fill/edge tone only — it is
  // 2.0:1 as text, so `destructive` above stays the tone that carries words.
  destructiveEdge: colors.alertEdge,

  darkSurfaceBg: colors.gray900, // tooltip bubble, undo bar
  darkSurfaceText: colors.gray50, // both use #FAFAFA, not pure white

  // cell-flash animation start color. Also hard-coded in index.css's
  // .cell-flash keyframe (0% background-color) — keep both in sync by hand.
  flashHighlight: colors.indigo100,

  // Modal backdrop scrim. LEFT AT 0.4 deliberately: it is a neutral black
  // scrim, not a palette colour, and 40% sits inside the 40-60% band a scrim
  // needs to isolate foreground content. Tinting it green would cast the
  // whole page behind every modal.
  overlay: "rgba(0,0,0,0.4)",
} as const;

// Not one of the 6 named categories, but a repeated design value found while
// cataloguing, so it's included for completeness.
export const shadows = {
  // The one "elevation" shadow — barely-there, for surfaces that should read
  // as a raised card over pageBg: DetailPanel, SidePanel (expanded), the
  // Inventory stat strip, and Settings' cards. Filter bars/info strips stay
  // flat (no shadow) on purpose — they're inline furniture, not cards.
  sm: "0 1px 2px rgba(16,24,40,0.06)",
  toggleKnob: "0 1px 2px rgba(0,0,0,0.12)", // the 3 Active toggle switches
  toast: "0 1px 2px rgba(0,0,0,0.08)", // UndoBar — same shape as toggleKnob, kept separate, see report
  selectedCellRing: "inset 0 0 0 1px #47801F", // MaterialTable selected-cell ring — colors.accentInk, hand-synced
  // ⚠️ NO CONSUMER SINCE D3a-ii. It marked a discount rule that named a
  // PROJECT, on a table that no longer exists -- anixify.discount.rule has no
  // project key at all, because nothing in this system identifies a project on
  // a quote line. Kept rather than deleted only because Sidebar.tsx's own
  // indicator-bar comment cites it as the precedent for that shape; if that
  // reference goes, this goes with it. Do NOT reach for it to mark something
  // else: an inset bar in accentInk means "has a project" to anyone who
  // remembers the screen it came from.
  projectIndicatorBar: "inset 2px 0 0 0 #47801F", // colors.accentInk, hand-synced
  // 3px inset alert bar shape shared by MaterialTable (brand violation) and
  // FreightRatesTable (expired row); the color differs by meaning, so build
  // the full value at the call site: `${shadows.alertBarShape} ${colors.red600}`.
  alertBarShape: "inset 3px 0 0 0",
  // index.css .tip-bubble box-shadow — documentation only, CSS can't import this.
  tooltip: "0 2px 6px rgba(0,0,0,0.18)",
  // NewQuoteModal's centered box. More pronounced than toast/tooltip above
  // since it sits over a dimmed backdrop, but still no heavy drop shadow.
  modal: "0 8px 24px rgba(0,0,0,0.15)",
  // ADDITIVE, slice ui-redesign. Focus ring for the redesigned modal's
  // inputs: a soft accent halo OUTSIDE the 1px border, so focus is legible
  // without the border itself having to carry it. Never used as the only
  // focus signal — the border colour changes to the accent as well, because
  // a halo alone is a colour-only cue.
  focusRing: "0 0 0 3px rgba(158,227,101,0.45)",
  // Same halo in the alert tone, for a field the server rejected.
  focusRingAlert: "0 0 0 3px rgba(236,161,148,0.45)",
  // ADDITIVE, slice login-redesign. A SOFTER elevation than `modal` above.
  // Deliberately its own value rather than reusing `modal`: that shadow is
  // tuned for a panel sitting over a dimmed backdrop, where a heavy drop reads
  // as "this is in front of the scrim". The sign-in card has NO scrim behind
  // it, so the same shadow reads as a smudge. This is the two-layer form a
  // floating card wants — a tight contact shadow plus a wide, very light
  // ambient one — and it is the only shadow on that screen.
  card: "0 1px 2px rgba(16,24,40,0.04), 0 12px 32px rgba(16,24,40,0.08)",
} as const;

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

export const typography = {
  // Set once in index.css (html/body). No component sets this inline, so
  // there's nothing to wire up in components — kept here for reference.
  fontFamily: '"InterVariable", "Inter", ui-sans-serif, system-ui, sans-serif',

  fontSize: {
    xs: 11,
    sm: 12,
    base: 13,
    md: 14,
    lg: 15,
    xl: 18,
    xxl: 20,
    // ADDITIVE, slice ui-redesign — the redesigned modal's title. Nothing
    // else in the app uses it, so no existing screen moves.
    xxxl: 24,
  },

  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
  },

  // Kept as 3 separate values rather than merged — see report.
  letterSpacing: {
    tight: "-0.01em", // page titles
    wide: "0.04em", // uppercase table headers / field labels / tabs — the common case, 12 uses
    wider: "0.05em", // SidePanel's 5 section headers only
    widest: "0.08em", // SidePanel's "Quote Analysis" top title only
  },

  lineHeight: {
    tight: 1, // undo bar / status pill line-height
    base: 1.45, // index.css body line-height — reference only, not set inline anywhere
  },
} as const;

// ---------------------------------------------------------------------------
// Spacing — generic gap / padding / margin scale, keyed by its own pixel value
// ---------------------------------------------------------------------------

export const spacing = {
  2: 2,
  3: 3, // MaterialTable's in-stock pill padding only
  4: 4,
  6: 6,
  8: 8,
  10: 10,
  12: 12,
  14: 14,
  16: 16,
  20: 20,
  24: 24,
  32: 32,
  40: 40, // shared "no rows match" empty-state padding across every table
} as const;

// ---------------------------------------------------------------------------
// Radius
// ---------------------------------------------------------------------------

export const radius = {
  sm: 2, // SidePanel sourcing-split color swatch
  md: 4, // in-stock pill in MaterialTable's description cell
  base: 6, // the app's default radius — cards, buttons, inputs, pills, tables (60 uses)
  // ADDITIVE, slice ui-redesign. `base` is deliberately untouched: it is the
  // app's default at ~60 call sites and moving it would restyle every screen
  // from a token edit. These two exist for the redesigned modal chrome only.
  lg: 10, // buttons and inputs in the redesigned modal
  xl: 20, // the modal panel itself
  full: 9999, // fully-round pill/circle shapes — merges former 999 and 9999, see report
} as const;

// ---------------------------------------------------------------------------
// Heights — fixed heights of controls, rows, and chrome
// ---------------------------------------------------------------------------

export const heights = {
  dot: 8, // SummaryStrip status dots, SidePanel sourcing-split bar
  legendDot: 10, // SidePanel sourcing-split legend swatch
  icon: 16, // toggle/radio knob circle, small close-icon touch target
  pill: 20, // status/category/lock-type/type pills, toggle switch track
  // SidePanel's 2 collapse/expand icon buttons. Coincidentally also 20, same
  // as `pill` above — different concept (icon touch target vs. pill height),
  // kept as its own token.
  iconButtonSm: 20,
  chip: 22, // SidePanel Sea/Air mode toggle, "add brand" chip button
  iconButton: 24, // close (X) buttons, chip remove buttons, small numeric inputs
  avatarSearch: 28, // TopBar search box and avatar circle
  input: 30, // standard text input / select
  // ADDITIVE, slice ui-redesign. Both are NEW KEYS — no existing height value
  // is touched, so SummaryStrip's literal minHeight:"36px" assertion and every
  // row/control height in the app are untouched by definition.
  inputLg: 52, // the redesigned modal's notched-outline fields
  controlXl: 44, // the redesigned modal's footer buttons — also the 44px touch-target floor
  control: 32, // standard button, tab row, sidebar nav item, table header row
  controlLg: 34, // side panel footer primary button ("Generate Quote PDF")
  // TopBar's logo image. Coincidentally also 24, same as iconButton above —
  // different concept (logo mark height vs. icon touch target), kept as its
  // own token since the two have no design relationship.
  topBarLogo: 24,
  // sidebar-and-logo: the mark in the sidebar header (was topBarLogo, 24 --
  // "too small and disconnected"). Its left edge is the nav icons' left edge.
  sidebarLogo: 32,
  // QuotePdfPreview's document-header logo — larger, for a printed page.
  // Coincidentally also 40, same as spacing[40] — unrelated (a height, not
  // a spacing value), kept separate for the same reason.
  pdfLogo: 40,
  // ADDITIVE, slice login-redesign. The sign-in card's logo. Its own token
  // rather than reusing pdfLogo above: that value is tuned for a printed A4
  // header and the two have no design relationship, so a change to either
  // would silently move the other. Larger here because this is the ONLY
  // brand mark on the screen and it is the visual anchor of the card —
  // ⚠️ the asset is a 500x500 SQUARE MARK with no wordmark in it (see the
  // note in the logo section below), so at pdfLogo's 40 it renders 40px wide
  // and reads as an icon rather than a brand.
  loginLogo: 56,
  row: 36, // table data row, table footer row, SummaryStrip, warning strips
  // MaterialPickerTable's row. Taller than the standard `row` above because
  // each row hosts a Qty number input plus an optional 2-line "remainder
  // will be sourced" note stacked beneath it — the standard 36px row has no
  // room for that second line.
  pickerRow: 58,
  filterBar: 44, // filter bar
  topBar: 48, // app top bar
} as const;

// ---------------------------------------------------------------------------
// Layout — fixed widths that repeat identically across files
// ---------------------------------------------------------------------------

export const layout = {
  // sidebar-and-logo (2026-09-30): sized to "Readiness" + its badge with
  // comfortable padding (it was 200 and mostly empty).
  sidebarWidth: 168,
  // Viewport width at and above which the full sidebar (labels) is the default;
  // between sidebarAutoCollapseBelow and this it is the icon rail.
  sidebarFullAbove: 1280,
  sidebarWidthCollapsed: 56, // icon-only rail width when the sidebar is collapsed
  // Viewport width below which the sidebar auto-collapses to its icon rail.
  // At 375px an expanded 200px sidebar leaves <main> only 175px — under half
  // the screen — so the rail is the only usable option on a phone. Uses the
  // existing collapse mechanism rather than a separate mobile drawer, so
  // there is still exactly one way the sidebar can be narrow.
  sidebarAutoCollapseBelow: 768,
  // app review P2-13: below this width the quote's analysis panel stacks
  // UNDER the quote content (collapsed) instead of beside it.
  quoteStackBelow: 768,
  panelWidthAnalysis: 360, // SidePanel (Quote Analysis)
  panelWidthDetail: 420, // Product / BrandLock / Discount / FreightRate detail panels — identical in all 4
  panelRailWidth: 28, // collapsed SidePanel rail
  // TopBar's search box. Coincidentally also 420, same as panelWidthDetail
  // above — kept as its own token since the two have no design relationship
  // and merging them would make either one accidentally change the other.
  topBarSearchWidth: 420,
  topBarAccountMinWidth: 120, // TopBar's right-hand account area
  // The 3 Active toggle switches' track width. Coincidentally also 34, same
  // as heights.controlLg below — again, no design relationship, kept apart.
  toggleTrackWidth: 34,
  // Catalog.tsx and QuoteDetail.tsx both compute their virtualized table's
  // height as Math.max(minTableHeight, window.innerHeight - tableHeightOffset).
  minTableHeight: 320,
  tableHeightOffset: 260,
  // Filter-bar search box minWidth. Inconsistent in the source itself:
  // Catalog.tsx uses 220, while Pricing.tsx (x2) and Sourcing.tsx use 200.
  // Preserved as two separate values rather than harmonized.
  searchMinWidthWide: 220, // Catalog.tsx only
  searchMinWidth: 200, // Pricing.tsx (both tabs), Sourcing.tsx
  // SidePanel's two free-standing numeric inputs (FX rate, negotiation
  // buffer) — one-off widths, each used in exactly one place.
  fxRateInputWidth: 80,
  // gp-per-quote: the quote header's GP % input ("28.0" plus spinner).
  gpInputWidth: 72,
  negotiationBufferInputWidth: 100,
  // The freight-bill input on an import shipment card. Wider than the two
  // above because it holds a whole invoice total rather than a rate or a
  // percentage, and it sits beside its currency code rather than alone.
  shipmentBillWidth: 120,
  // Square preview box for the company logo in Settings > Company. Square
  // so a wide or tall logo is judged on the same footprint either way.
  logoPreviewBox: 96,
  // Hit area of MaterialTable's column-resize handle, sitting inside the
  // right edge of a resizable header cell. Wide enough to grab with a mouse
  // without eating into the header label; the visible divider inside it is
  // only 2px (spacing[2]).
  colResizeHandleWidth: 8,
  modalWidth: 480, // NewQuoteModal's centered box
  // ADDITIVE, slice login-redesign. The sign-in card. Narrower than
  // modalWidth on purpose: that width is sized for two-column modal FORMS,
  // while this card holds one column of two fields, and a 480px box around
  // two stacked inputs reads as an empty dialog rather than a login.
  loginCardWidth: 400,
  // ImportCsvModal's centered box. Deliberately its own token rather than a
  // bigger modalWidth: this one holds a per-row PREVIEW TABLE, and a row whose
  // error message wraps over three lines is one nobody reads. The two have no
  // design relationship, so widening the shared value to suit this screen
  // would silently stretch NewQuoteModal and NewProductModal too.
  // (Its per-column preview widths stay as plain numbers in the component,
  // under the same rule every table's COLS widths follow — they are that
  // table's schema, not shared design values.)
  importModalWidth: 880,
  pickerWidth: 720, // MaterialPicker's right-anchored slide-over
  // QuotePdfPreview's document page — CSS px at 96dpi for A4 portrait
  // (210mm x 297mm). This is a single continuous scrollable page, not real
  // print pagination, so pdfPageMinHeight is a floor, not an exact page break.
  pdfPageWidth: 794,
  pdfPageMinHeight: 1123,
} as const;

// ---------------------------------------------------------------------------
// The viewport cap for fixed-width overlays and panels
// ---------------------------------------------------------------------------
//
// ⚠️ EVERY OVERLAY IN THIS APP HAS A FIXED PIXEL WIDTH, and seven of them are
// wider than a phone. Measured against a 390px viewport:
//
//   panelWidthAnalysis  360   SidePanel, an INLINE aside beside the main column
//   panelWidthDetail    420   the six record detail panels, also inline asides
//   modalWidth          480   NewQuoteModal, NewProductModal
//   pickerWidth         720   the Add Materials slide-over
//   pdfPageWidth        794   the quote document page
//   importModalWidth    880   the CSV import preview
//
// ⚠️ loginCardWidth (400) IS NOT ON THAT LIST, and was checked rather than
// assumed. Login.tsx already carries maxWidth: "100%", and its wrapper has
// padding: spacing[24] -- so the card's effective cap is 100vw - 48px, MORE
// generous than the 16px gutter below. Applying this constant there would
// tighten the sign-in card for no reason.
//
// A box wider than the viewport does not merely look wrong: on the two inline
// asides it crushes the main column to nothing, and on the overlays it pushes
// its own close button off-screen, which is the difference between ugly and
// unusable.
//
// ⚠️ THIS IS A CAP, NOT RESPONSIVE DESIGN, AND THE DISTINCTION IS DELIBERATE.
// It is applied as `maxWidth` and the `width` declarations are left exactly as
// they were, so at any viewport at least 16px wider than the box the cap has
// NO EFFECT AT ALL -- `max-width` is inert whenever it exceeds the used width.
// That is why this is provably identical on every desktop width rather than
// merely believed to be: there is no breakpoint, no media query and no second
// layout to diverge.
//
// What it does NOT do, on purpose: it does not reflow anything, does not stack
// a two-column form, does not turn a slide-over into a bottom sheet and does
// not decide that a phone should be refused the quote screen. Those are design
// decisions, they need a browser to judge, and they are recorded in HANDOFF.md
// as deferred rather than guessed at here.
//
// ⚠️ NOT FOR THE VIRTUALIZED TABLES. MaterialTable, CatalogTable,
// MaterialPickerTable and InventoryTable size themselves from the sum of their
// COLS and scroll horizontally, which is CORRECT behaviour for a 20-column
// quote grid and must not be "fixed". A cap there would squeeze the columns
// instead of letting them scroll.
//
// 16px = spacing[8] on each side. A gutter rather than a flush fit, so the box
// still reads as a box on a narrow screen.
export const overlayMaxWidth = `calc(100vw - ${spacing[8] * 2}px)`;

// ---------------------------------------------------------------------------
// Icon sizes (lucide-react `size` / `strokeWidth` props)
// ---------------------------------------------------------------------------

export const iconSize = {
  xs: 11,
  sm: 12,
  md: 14,
  lg: 16,
} as const;

export const iconStroke = {
  regular: 1.5, // the standard weight used everywhere — 23 of 24 icons
  bold: 2, // brand-chip remove (X) button only
} as const;

// ---------------------------------------------------------------------------
// Motion
// ---------------------------------------------------------------------------

export const motion = {
  // Standard micro-interaction duration: toggle background-color, tooltip
  // opacity fade, button/row hover and press feedback. Differ in easing at
  // the call site — compose the full string there, e.g.
  // `background-color ${motion.fast}` or `opacity ${motion.fast} ease-out`.
  fast: "120ms",
  // Layout-shift transitions (sidebar collapse width, and <main>'s
  // marginLeft following it) — slower than motion.fast's micro-interaction
  // duration so a ~144px width change reads as a deliberate slide rather
  // than a flicker. Kept as its own token rather than reusing fast, since
  // the two serve different classes of motion (hover feedback vs. layout
  // reflow).
  layoutShift: "200ms",
  // index.css .cell-flash keyframe duration — documentation only, CSS can't
  // import this.
  flashDurationMs: 300,
  // MaterialTable.tsx's setTimeout that removes the flash class.
  // Intentionally 20ms longer than flashDurationMs so the class isn't
  // removed before the CSS animation visually finishes. Kept separate from
  // flashDurationMs on purpose — see report.
  flashCleanupMs: 320,
} as const;
